interface FinancingOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  showEcsOffer?: boolean;
  companyId: number | undefined;
}

const COLUMNS = [
  'Banks',
  'Initial Deposit',
  'Maximum Amount',
  'Maximum Tenure',
  'Monthly int. rate',
  'Fee',
];

export default function FinancingOptionsModal({
  isOpen,
  onClose,
  showEcsOffer = false,
  companyId,
}: FinancingOptionsModalProps) {
  const FINANCING_OPTIONS = [
    {
      bank: 'Sparkle',
      logo: '/asset/partners/sparkle.jpeg',
      initialDeposit: '15%',
      maximumAmount: '₦12,000,000',
      maximumTenure: '12 months',
      monthlyInterestRate:
        companyId !== Number(import.meta.env.VITE_ECS_ID)
          ? '3.5%'
          : 'Up to 2.5%',
      fee: '2%',
    },
    {
      bank: 'Fast Credit',
      logo: '/asset/partners/fastcredit.png',
      initialDeposit: '4%',
      maximumAmount: '₦3,000,000',
      maximumTenure: '12 months',
      monthlyInterestRate:
        companyId !== Number(import.meta.env.VITE_ECS_ID)
          ? '4.0%'
          : 'Up to 3.0%',
      fee: '2.5%',
    },
  ];

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center
      bg-black bg-opacity-50 z-[1000] px-[16px]"
    >
      <div
        className="bg-white rounded-lg shadow-lg w-full max-w-[840px]
        max-h-[90vh] overflow-y-auto p-[20px] font-dmsans"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-[18px] text-[#171334] font-[500]">
            Financing Options
          </h2>
          <button
            type="button"
            aria-label="close financing options"
            onClick={onClose}
            className="w-[20px] h-[20px] rounded-full border-[1.5px] border-[#2563EB]
            text-[#2563EB] flex items-center justify-center text-[12px] leading-none
            bg-transparent p-0"
          >
            ✕
          </button>
        </div>

        <p className="text-[#8A87A0] text-[14px] mt-[16px] mb-[10px] px-[10px]">
          Key Details.
        </p>

        <div className="border border-gray-200 rounded-lg overflow-x-auto">
          <table className="w-full min-w-[640px] text-[13px] text-center">
            <thead>
              <tr className="bg-[#2563EB] text-white">
                {COLUMNS.map((column, index) => (
                  <th
                    key={column}
                    className={`font-[600] py-[16px] px-[12px] ${
                      index === 0 ? 'text-left' : ''
                    }`}
                  >
                    {column === 'Fee' ? 'Bank Fee' : column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FINANCING_OPTIONS.map((option, index) => (
                <tr
                  key={option.bank}
                  className={`border-t border-gray-200 text-[#171334] ${
                    index % 2 === 1 ? 'bg-[#F5F5F7]' : 'bg-white'
                  }`}
                >
                  <td className="py-[12px] px-[12px]">
                    <img
                      src={option.logo}
                      alt={option.bank}
                      className="h-[30px] object-contain"
                    />
                  </td>
                  <td className="py-[12px] px-[12px]">
                    {option.initialDeposit}
                  </td>
                  <td className="py-[12px] px-[12px]">
                    {option.maximumAmount}
                  </td>
                  <td className="py-[12px] px-[12px]">
                    {option.maximumTenure}
                  </td>
                  <td className="py-[12px] px-[12px]">
                    {option.monthlyInterestRate}
                  </td>
                  <td className="py-[12px] px-[12px]">{option.fee}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {showEcsOffer && (
          <div
            className="bg-[#F5F7FF] border border-[#DCE6FF] rounded-lg
            p-[12px] mt-[12px] text-[#171334] text-[13px] font-[500]"
          >
            <div className="flex gap-[16px] items-center">
              <img
                src="/asset/ecs.png"
                alt="fers"
                className="rounded-full w-[32px] h-[32px] object-contain"
              />
              <div className="flex flex-col gap-[4px]">
                <div>
                  <p className="font-[700] text-[12px] text-[#2563EB]">
                    FERS Exclusive Offer
                  </p>
                </div>
                <div
                  className="bg-[#2563EB] text-white font-[600] text-[12px]
                  px-[10px] py-[4px] rounded-full w-fit whitespace-nowrap"
                >
                  12% OFF Total Interest Rate
                </div>
              </div>
            </div>

            <div className="mt-[12px] pl-[48px] space-y-[8px] text-[12px]">
              <div>
                <p>
                  Get a 12% discount on your total Pay Small Small interest when
                  you pay in instalments over 3–12 months.
                </p>
              </div>
              <div>
                <p className="font-[700]">Example: How Your Discount Works</p>
                <p>
                  If your standard interest rate is 3.5% per month and you
                  choose a 12-month repayment plan
                </p>
              </div>
              <ul className="space-y-[2px]">
                <li>
                  <span className="font-[600]">Standard total interest:</span>{' '}
                  3.5% × 12 months = 42%
                </li>
                <li>
                  <span className="font-[600]">Interest discount:</span> 12%
                </li>
                <li>
                  <span className="font-[600]">Discounted total interest:</span>{' '}
                  42% − 12% = 30%
                </li>
                <li>
                  <span className="font-[600]">
                    Effective monthly interest:
                  </span>{' '}
                  30% ÷ 12 months = 2.5%
                </li>
              </ul>
              <p className="font-[700] text-[#2563EB]">
                You pay 2.5% interest instead of 3.5% monthly.
              </p>
              <p className="italic text-gray-500">
                Your actual interest rate and repayment terms may vary depending
                on the financing provider and repayment period.
              </p>
            </div>
          </div>
        )}

        <div className="flex justify-end mt-[20px]">
          <div
            className="text-center font-bold uppercase bg-black text-white
            px-6 py-2 rounded-full cursor-pointer"
            onClick={onClose}
          >
            Continue
          </div>
        </div>
      </div>
    </div>
  );
}
